import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from '@/lib/api';

/**
 * Currencies are group-level configuration, so the key carries no section (docs/03 §5).
 * The list is read by every money screen, which is why `staleTime` is generous: a rate
 * that is sixty seconds old is a rate, and re-fetching it on every form mount would put a
 * request behind every navigation.
 */
export const currencyKeys = {
  all: ['currencies'],
  list: (filters = {}) => ['currencies', 'list', filters],
};

export const useCurrencies = (filters = {}, options = {}) =>
  useQuery({
    queryKey: currencyKeys.list(filters),
    queryFn: async () => (await get('/currencies', { params: filters })).data,
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useCreateCurrency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await post('/currencies', body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};

export const useUpdateCurrency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await patch(`/currencies/${id}`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};

/**
 * Re-rating is its own mutation because it is its own act on the server.
 *
 * Nothing already posted changes — every entry carries the rate it posted at — so only
 * the currency list is invalidated here, not the reports.
 */
export const useSetCurrencyRate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }) => (await post(`/currencies/${id}/rate`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};

/**
 * Moving the base currency re-denominates every report, so this drops the **whole** cache
 * rather than one key. The server refuses it once anything has posted; when it does
 * succeed, nothing already on screen is still true.
 */
export const useSetBaseCurrency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await post(`/currencies/${id}/base`)).data,
    onSuccess: () => queryClient.clear(),
  });
};

export const useDeleteCurrency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await del(`/currencies/${id}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: currencyKeys.all }),
  });
};
